/**
 * Crew roster + crews for the Tagger, cached per project so crew codes still preview offline.
 */
import { warn } from '@utils/console';
import { CREW_SEATS, normalizeCrewName } from '@utils/crewCode';
import { fetchCrewRoster, type CrewRosterRow } from './crewRosterService';
import { fetchCrews, type CrewRow } from './crewService';

const STORAGE_PREFIX = 'racesight_tagger_crew_roster_v1:p:';

export type TaggerCrewData = { roster: CrewRosterRow[]; crews: CrewRow[] };

type CachedCrewData = TaggerCrewData & { className: string; fetchedAt: string };

const EMPTY: TaggerCrewData = { roster: [], crews: [] };

export function loadCachedTaggerCrewRoster(projectId: number): CachedCrewData | null {
  if (typeof localStorage === 'undefined') {return null;}
  try {
    const raw = localStorage.getItem(`${STORAGE_PREFIX}${projectId}`);
    if (!raw) {return null;}
    const parsed = JSON.parse(raw) as Partial<CachedCrewData>;
    if (!Array.isArray(parsed.roster) || !Array.isArray(parsed.crews) || typeof parsed.className !== 'string') {
      return null;
    }
    return {
      className: parsed.className,
      roster: parsed.roster,
      crews: parsed.crews,
      fetchedAt: String(parsed.fetchedAt ?? ''),
    };
  } catch (e) {
    warn('[taggerCrewRoster] cache read failed', e);
    return null;
  }
}

/** Keep a just-created crew in the offline cache so the tagger preview still resolves it. */
export function rememberTaggerCrewRoster(projectId: number, className: string, data: TaggerCrewData): void {
  const cls = className.trim().toLowerCase();
  if (!cls) {return;}
  saveCachedTaggerCrewRoster(projectId, {
    className: cls,
    roster: data.roster,
    crews: data.crews,
    fetchedAt: new Date().toISOString(),
  });
}

function saveCachedTaggerCrewRoster(projectId: number, value: CachedCrewData): void {
  if (typeof localStorage === 'undefined') {return;}
  try {
    localStorage.setItem(`${STORAGE_PREFIX}${projectId}`, JSON.stringify(value));
  } catch (e) {
    warn('[taggerCrewRoster] cache write failed', e);
  }
}

/**
 * Fresh roster + crews when the server answers both, else the cached pair, else empty lists.
 */
export async function refreshTaggerCrewRoster(projectId: number, className: string): Promise<TaggerCrewData> {
  const cached = loadCachedTaggerCrewRoster(projectId);
  const fallback = cached ? { roster: cached.roster, crews: cached.crews } : EMPTY;
  const cls = (className || cached?.className || '').toLowerCase();
  if (!cls || (typeof navigator !== 'undefined' && !navigator.onLine)) {
    return fallback;
  }
  const [roster, crews] = await Promise.all([fetchCrewRoster(cls, projectId), fetchCrews(cls, projectId)]);
  if (roster == null || crews == null) {
    return fallback;
  }
  saveCachedTaggerCrewRoster(projectId, { className: cls, roster, crews, fetchedAt: new Date().toISOString() });
  return { roster, crews };
}

const PENDING_KEY = 'racesight_tagger_pending_crew_options_v1';

/** A line-up saved while offline (or before the roster loaded) that still needs a crew option. */
export type PendingCrewOption = {
  projectId: number;
  className: string;
  tags: Record<string, string>;
};

function pendingLineupKey(item: PendingCrewOption): string {
  const seats = CREW_SEATS.map((seat) => normalizeCrewName(item.tags[seat] ?? '')).join('|');
  return `${item.projectId}|${item.className.trim().toLowerCase()}|${seats}`;
}

function readPendingCrewOptions(): PendingCrewOption[] {
  if (typeof sessionStorage === 'undefined') {return [];}
  try {
    const raw = sessionStorage.getItem(PENDING_KEY);
    if (!raw) {return [];}
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) {return [];}
    return parsed.filter((item): item is PendingCrewOption => {
      if (item == null || typeof item !== 'object') {return false;}
      const row = item as Partial<PendingCrewOption>;
      return typeof row.projectId === 'number' && typeof row.className === 'string' && row.tags != null && typeof row.tags === 'object';
    });
  } catch (e) {
    warn('[taggerCrewRoster] pending crew read failed', e);
    return [];
  }
}

function writePendingCrewOptions(items: PendingCrewOption[]): void {
  if (typeof sessionStorage === 'undefined') {return;}
  try {
    if (items.length === 0) {
      sessionStorage.removeItem(PENDING_KEY);
      return;
    }
    sessionStorage.setItem(PENDING_KEY, JSON.stringify(items));
  } catch (e) {
    warn('[taggerCrewRoster] pending crew write failed', e);
  }
}

/** Remember a line-up whose crew option could not be created yet. Same seats replace the previous entry. */
export function queuePendingCrewOption(item: PendingCrewOption): void {
  const key = pendingLineupKey(item);
  const next = readPendingCrewOptions().filter((row) => pendingLineupKey(row) !== key);
  next.push(item);
  writePendingCrewOptions(next);
}

export function loadPendingCrewOptions(): PendingCrewOption[] {
  return readPendingCrewOptions();
}

export function replacePendingCrewOptions(items: PendingCrewOption[]): void {
  writePendingCrewOptions(items);
}
